import { sessionApi } from './apiManager';
import { useState, useEffect, useCallback } from 'react';
import type { 
  SessionHistoryResponse, 
  UpdateLastPlayedResponse 
} from '../types/sessions/service/types';
export type { SessionHistoryItem } from '../types/sessions/service/types';

// types moved to ../types/sessions/service

export const audioHistoryService = {
  async fetchUserSessions(
    userId: string | number,
    page: number = 1,
    limit: number = 5,
    type: 'v' | 'b' = 'v'
  ): Promise<SessionHistoryResponse> {
    try {
      const response = await sessionApi.fetchUserSessions(userId, page, limit, type);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch user sessions');
      }

      const result = response.data;
      const hasMore = result?.length === limit; 
      
      return {
        data: result ?? [] as any[],
        total: result?.length ?? 0, 
        page: page,
        limit: limit,
        hasMore: hasMore
      };
    } catch (error) {
      throw error;
    }
  },

  async updateLastPlayed(
    sessionId: number,
    typeSession: 'v' | 'b'
  ): Promise<UpdateLastPlayedResponse> {
    try {
      const response = await sessionApi.updateLastPlayed(sessionId, typeSession);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to update last played');
      }

      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  async updateListened(
    sessionId: number,
    valor: boolean,
    typeSession: 'v' | 'b'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await sessionApi.updateListened(sessionId, valor, typeSession);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to update listened status');
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  async deleteSession(
    userId: string | number,
    sessionId: number,
    typeSession: 'v' | 'b'
  ): Promise<void> {
    try {
      const response = await sessionApi.deleteSession(userId, sessionId, typeSession);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Error:', error instanceof Error ? error.message : 'Unknown error');
    }
  },
};

export const useSessionHistory = (
  userId: string | number,
  page: number = 1,
  limit: number = 50,
  type: 'v' | 'b' = 'v'
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState<SessionHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(page);
  const [deletingSessions] = useState<Set<number>>(new Set());
  const [hasMorePages, setHasMorePages] = useState(true);

  const fetchSessions = useCallback(async (pageNum: number = currentPage) => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const result = await audioHistoryService.fetchUserSessions(userId, pageNum, limit, type);
      setSessions(result);
      setCurrentPage(pageNum);
      
      // Set hasMorePages based on whether we got a full page
      if (result.data && result.data.length < limit) {
        setHasMorePages(false);
      }
    } catch (err) {
      setError('Unable to load sessions. Please check your connection.');
      // Simple message instead of detailed error logging
      console.log('Sessions not available');
    } finally {
      setIsLoading(false);
    }
  }, [userId, limit, type]);

  const loadMore = useCallback(async () => {
    if (!userId || isLoading || !hasMorePages) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const nextPage = currentPage + 1;
      const result = await audioHistoryService.fetchUserSessions(userId, nextPage, limit, type);
      
      if (result.data && result.data.length > 0) {
        setSessions(prev => prev ? {
          ...result,
          data: [...prev.data, ...result.data],
          hasMore: result.data.length === limit
        } : result);
        setCurrentPage(nextPage);

        if (result.data.length < limit) {
          setHasMorePages(false);
        }
      } else {
        setHasMorePages(false);
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        setHasMorePages(false);
      } else {
        setError('Unable to load more sessions.');
        console.log('More sessions not available');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, hasMorePages, isLoading, currentPage, limit, type]);

  const deleteSession = useCallback(
    async (sessionId: number) => {
      if (!userId || deletingSessions.has(sessionId)) return;

      deletingSessions.add(sessionId);

      try {
        setSessions((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            data: prev.data.filter((session) => {
              const matchesBySessionId =
                session.sessionId !== undefined &&
                session.sessionId !== null &&
                Number(session.sessionId) === Number(sessionId);
              const matchesByPrimaryId =
                session.id !== undefined &&
                Number(session.id) === Number(sessionId);
              return !(matchesBySessionId || matchesByPrimaryId);
            }),
          };
        });

        await audioHistoryService.deleteSession(userId, sessionId, type);
      } catch (error) {
        console.log('Session deletion failed');
        await fetchSessions(currentPage);
      } finally {
        deletingSessions.delete(sessionId);
      }
    },
    [userId, type, deletingSessions, fetchSessions, currentPage]
  );

  useEffect(() => {
    if (userId) {
      fetchSessions(page);
    }
  }, [userId, page, fetchSessions]);

  const optimisticBumpSession = useCallback((sessionId: number) => {
    try {
      
      setSessions(prev => {
        if (!prev) {
          return prev;
        }
        const items = [...prev.data];
        
        const index = items.findIndex(i => {
          const matchesBySessionId = i.sessionId !== undefined && i.sessionId !== null && Number(i.sessionId) === Number(sessionId);
          const matchesByPrimaryId = i.id !== undefined && Number(i.id) === Number(sessionId);
          return matchesBySessionId || matchesByPrimaryId;
        });
        
        if (index === -1) {
          return prev;
        }
        
        const updated = { ...items[index], lastPlayback: new Date().toISOString() };
        items.splice(index, 1);
        items.unshift(updated);
        
        return { ...prev, data: items };
      });
    } catch (error) {
      console.log('Session update failed');
    }
  }, []);

  return {
    isLoading,
    sessions,
    error,
    currentPage,
    hasMore: hasMorePages,
    deletingSessions,
    deleteSession,
    loadMore,
    refetch: () => fetchSessions(currentPage),
    optimisticBumpSession,
  };
}; 